import { Box, Flex, Text } from '@chakra-ui/react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Icon } from '../../../../components/Icon'
import { LiveArtwork } from '../../../../services/Event/types'
import { SoldOverlay } from './SoldOverlay'
import { ROTATION_INTERVAL_MS } from './useAuctionScreen'

/** Proporção máxima do quadro (largura ÷ altura), como no mockup. */
const FRAME_MAX_RATIO = 1.85

/** Velocidade da legenda que não cabe na linha, em px por segundo. */
const CAPTION_SCROLL_SPEED = 55

/** Tempo parado em cada ponta antes de a legenda voltar a correr, em segundos. */
const CAPTION_SCROLL_DWELL = 1

/**
 * Transbordo mínimo para valer a pena rolar. Abaixo disso a legenda fica parada: mexer
 * por causa de meia palavra chama mais atenção do que o pedaço escondido.
 */
const CAPTION_MIN_OVERFLOW = 8

/**
 * Duração mínima do ciclo, em segundos. Sem ela, um transbordo pequeno gera um ciclo de
 * ~2,7s — e, descontadas as duas pausas, sobram 0,36s para cada trecho: a legenda vai e
 * volta em solavancos várias vezes por segundo em vez de correr devagar.
 */
const CAPTION_MIN_CYCLE = 7

interface IFeaturedArtworkProps {
  artwork: LiveArtwork | null
}

/**
 * A obra em destaque. O quadro tem proporção fixa e a imagem entra com `contain`:
 * é o que garante o requisito de enquadramento do SDD — a arte nunca estoura os
 * limites do quadro e uma imagem vertical fica centralizada no espaço reservado.
 *
 * A legenda mora dentro do quadro, presa ao rodapé, sobre um degradê: fora dele ela
 * roubava altura da obra e deixava a base do quadro desalinhada do card de lances.
 */
export function FeaturedArtwork({ artwork }: IFeaturedArtworkProps) {
  const caption = [
    artwork?.author_name,
    artwork?.name,
    [artwork?.technique, artwork?.dimensions].filter(Boolean).join(', ') || null
  ].filter(Boolean) as string[]

  const captionBoxRef = useRef<HTMLDivElement>(null)
  const captionTextRef = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState(0)

  // Quanto a legenda passa da largura disponível. É esse excedente que a animação
  // percorre — a legenda nunca quebra linha, ela corre e volta.
  useLayoutEffect(() => {
    const measure = () => {
      const box = captionBoxRef.current
      const text = captionTextRef.current
      if (!box || !text) return

      setOverflow(Math.max(0, Math.ceil(text.scrollWidth - box.clientWidth)))
    }

    measure()

    const observer = new ResizeObserver(measure)
    if (captionBoxRef.current) observer.observe(captionBoxRef.current)

    return () => observer.disconnect()
  }, [caption.join(' | ')])

  // Reinicia a animação a cada troca de obra, para a legenda nova começar do início.
  const [scrollKey, setScrollKey] = useState(0)
  useEffect(() => setScrollKey((key) => key + 1), [artwork?.id])

  // A ida e a volta têm de caber no tempo que a obra fica em destaque: no ritmo natural
  // uma legenda longa levaria mais que os 10s da rotação e nunca voltaria ao início.
  const shouldScroll = overflow >= CAPTION_MIN_OVERFLOW
  const naturalSeconds = (overflow / CAPTION_SCROLL_SPEED) * 2 + CAPTION_SCROLL_DWELL * 2
  const totalSeconds = Math.min(Math.max(naturalSeconds, CAPTION_MIN_CYCLE), ROTATION_INTERVAL_MS / 1000)
  const travelSeconds = Math.max(0, (totalSeconds - CAPTION_SCROLL_DWELL * 2) / 2)
  const dwellRatio = CAPTION_SCROLL_DWELL / totalSeconds
  const travelRatio = travelSeconds / totalSeconds

  /**
   * O nome do `@keyframes` tem de ser único por animação. Dois blocos de mesmo nome colidem
   * no documento e **o último declarado vence para todos os elementos** — e a Emotion emite
   * um bloco por `sx` distinto. Com o nome fixo que estava aqui, cada obra sobrescrevia a
   * anterior: a legenda longa andava a distância da legenda de outra obra, indo só um pedaço
   * e nunca mostrando o fim (ou passando do fim, quando a que venceu era maior).
   */
  const animationName = `captionScroll${Math.round(totalSeconds * 100)}x${overflow}`

  /*
    O quadro toma a altura disponível e deriva a largura dela, com teto de proporção.
    Sem esse teto ele acompanha a largura da coluna e vira uma faixa deitada (2.2:1 numa
    tela 16:9), o que enche a moldura de cinza quando a obra é vertical.

    Alinhado à esquerda, não centralizado: quando o teto de proporção deixa folga na
    coluna, o quadro centralizado escorrega para a direita e desalinha do selo "AO VIVO",
    que nasce na margem da página.
  */
  return (
    <Flex flex="1" minH={0} justify="flex-start">
      <Box
        h="100%"
        maxW="100%"
        sx={{ aspectRatio: `${FRAME_MAX_RATIO}` }}
        bg="#C6C6C6"
        borderRadius="lg"
        overflow="hidden"
        position="relative"
      >
        {artwork?.art_image_url ? (
          <Box
            as="img"
            src={artwork.art_image_url}
            alt={artwork.name}
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="contain"
          />
        ) : (
          <Flex align="center" justify="center" h="100%" color="blackAlpha.600">
            <Icon name="lni lni-image" fontSize="3vw" />
          </Flex>
        )}

        {artwork?.sale_status === 'sold' && <SoldOverlay artwork={artwork} variant="featured" />}

        {/*
          O degradê é o que segura o branco legível: numa obra vertical o `contain` deixa a
          tarja cinza justamente onde a legenda cai, e branco sobre #C6C6C6 não se lê. Depois
          do selo e com `zIndex`, para a legenda continuar visível na obra arrematada.
        */}
        {caption.length > 0 && (
          <Box
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            zIndex={1}
            pt="9vh"
            px="1.7vw"
            pb="1.6vh"
            bgGradient="linear(to-t, blackAlpha.600, blackAlpha.300 45%, transparent)"
            pointerEvents="none"
          >
            {/*
              Uma linha só. O que não couber corre para a frente e volta devagar ao início.
              O padding fica na faixa, não aqui: `clientWidth` conta o padding, e medir por
              dentro dele faria o fim da legenda parar escondido sob a margem direita.
            */}
            <Box ref={captionBoxRef} overflow="hidden">
              <Flex
                key={scrollKey}
                ref={captionTextRef}
                display="inline-flex"
                align="baseline"
                gap={2}
                whiteSpace="nowrap"
                color="white"
                fontSize="clamp(12px, 1.15vw, 26px)"
                textShadow="0 1px 3px rgba(0, 0, 0, 0.55)"
                sx={
                  shouldScroll
                    ? {
                        animation: `${animationName} ${totalSeconds.toFixed(2)}s ease-in-out infinite`,
                        [`@keyframes ${animationName}`]: {
                          '0%': { transform: 'translateX(0)' },
                          [`${(dwellRatio * 100).toFixed(2)}%`]: { transform: 'translateX(0)' },
                          [`${((dwellRatio + travelRatio) * 100).toFixed(2)}%`]: {
                            transform: `translateX(-${overflow}px)`
                          },
                          [`${((dwellRatio * 2 + travelRatio) * 100).toFixed(2)}%`]: {
                            transform: `translateX(-${overflow}px)`
                          },
                          '100%': { transform: 'translateX(0)' }
                        }
                      }
                    : {}
                }
              >
                {caption.map((part, index) => (
                  <Text key={part} fontWeight={index === 1 ? '700' : '400'} opacity={index === 2 ? 0.75 : 1}>
                    {index > 0 && (
                      <Box as="span" color="whiteAlpha.500" mr={2}>
                        |
                      </Box>
                    )}
                    {part}
                  </Text>
                ))}
              </Flex>
            </Box>
          </Box>
        )}
      </Box>
    </Flex>
  )
}
